import {
  AgentThreadMessage,
  AgentThreadRole,
  isAgentThreadTextContent,
  isAgentThreadClientContextContent,
  ClientViewingContext,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { VOLATILE_VISUALIZER_STATE_FIELDS } from "@/shared/services/ViewingContextService";

import { isEmptyContext } from "../contextUtils";

const CTX_REGEX = /^<ctx>The above was sent with context: (.+)<\/ctx>$/;

/**
 * Whether a value is usable as a ClientViewingContext downstream.
 *
 * `isAgentThreadClientContextContent` checks only the `content_type`
 * discriminator, so a malformed block can pass it with `context` undefined or
 * missing the id arrays (review finding 63). Everything downstream —
 * `isEmptyContext`, `contextsEqual`, the shift indicator — indexes
 * `dataset_ids`/`file_ids` unconditionally, so surfacing such a value makes
 * `scanForShifts` throw instead of degrade. Validate the minimal shape those
 * consumers rely on and let anything else read as "no context".
 */
function isValidClientViewingContext(
  value: unknown,
): value is ClientViewingContext {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { dataset_ids?: unknown }).dataset_ids) &&
    Array.isArray((value as { file_ids?: unknown }).file_ids)
  );
}

/**
 * A stored context is whatever the client recorded at send time, which for an
 * existing thread predates fields added since — a thread from before
 * collection/device/session/device-type context existed carries no such keys. Every
 * id list is defaulted to `[]` here, at the single point untrusted stored JSON enters, so
 * downstream readers can take `.length` unconditionally.
 */
function withDefaultedIdLists(
  context: ClientViewingContext,
): ClientViewingContext {
  return {
    ...context,
    dataset_ids: context.dataset_ids ?? [],
    file_ids: context.file_ids ?? [],
    collection_ids: context.collection_ids ?? [],
    device_ids: context.device_ids ?? [],
    device_type_ids: context.device_type_ids ?? [],
    session_ids: context.session_ids ?? [],
    view_ids: context.view_ids ?? [],
  };
}

/**
 * Recovers the ClientViewingContext a message carries, in either storage shape.
 *
 * Current: an `AgentClientContextEntry` block on the user's own message — a typed field, no
 * parsing. Legacy (pre-ENG-2185): a whole ROBOTO-role message whose text is a `<ctx>...</ctx>`
 * marker. Threads written before the change are still live and a single thread can span it, so
 * both are read, and both pass through `withDefaultedIdLists`.
 */
export function parseContextFromMessage(
  message: AgentThreadMessage,
): ClientViewingContext | null {
  for (const content of message.content) {
    if (isAgentThreadClientContextContent(content)) {
      // A block that passes the discriminator guard can still carry a missing
      // or malformed context — treat it as carrying none rather than letting
      // scanForShifts throw on it downstream.
      return isValidClientViewingContext(content.context)
        ? withDefaultedIdLists(content.context)
        : null;
    }
  }

  // Legacy marker: only ever written as its own ROBOTO-role message.
  if (message.role !== AgentThreadRole.ROBOTO) {
    return null;
  }

  for (const content of message.content) {
    if (!isAgentThreadTextContent(content)) {
      continue;
    }

    const match = content.text.match(CTX_REGEX);
    if (match) {
      try {
        const parsed: unknown = JSON.parse(match[1]);
        return isValidClientViewingContext(parsed)
          ? withDefaultedIdLists(parsed)
          : null;
      } catch {
        return null;
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Incremental scan infrastructure
// ---------------------------------------------------------------------------

export interface ScanState {
  previousContext: ClientViewingContext | null;
}

export interface ScanResult {
  shifts: Map<number, ClientViewingContext>;
  endState: ScanState;
}

/**
 * Scans messages starting at `fromIndex` and returns context shifts found,
 * plus the end state so callers can resume later without re-processing.
 *
 * Empty contexts are only emitted when transitioning from non-empty → empty
 * (user navigated away). Initial empty contexts (when previousContext is null
 * or also empty) are suppressed.
 */
export function scanForShifts(
  messages: AgentThreadMessage[],
  fromIndex: number,
  initialState: ScanState,
): ScanResult {
  const shifts = new Map<number, ClientViewingContext>();
  let previousContext = initialState.previousContext;

  for (let i = fromIndex; i < messages.length; i++) {
    const ctx = parseContextFromMessage(messages[i]);
    if (ctx === null) {
      continue;
    }

    // Skip empty contexts when there is no previous non-empty context
    // (i.e. the conversation starts with empty context — nothing to show).
    // But DO show empty contexts when transitioning from non-empty → empty,
    // since the user navigated away from something.
    if (
      isEmptyContext(ctx) &&
      (previousContext === null || isEmptyContext(previousContext))
    ) {
      previousContext = ctx;
      continue;
    }

    if (!contextsEqual(previousContext, ctx)) {
      // Which message to hang the indicator on. Since ENG-2185 the context is a block on the
      // user's own message, so that message IS the one — start the walk at `i`, not `i - 1`.
      // Legacy threads carry it as a separate ROBOTO message after the user's, where the walk has
      // to step back to find them; starting at `i` handles both, because a USER-role carrier
      // matches itself immediately.
      const userIndex = findPrecedingUserMessage(messages, i);
      if (userIndex !== null) {
        shifts.set(userIndex, ctx);
      }
    }
    previousContext = ctx;
  }

  return { shifts, endState: { previousContext } };
}

// ---------------------------------------------------------------------------
// Public API — convenience wrapper for full-scan use cases and tests
// ---------------------------------------------------------------------------

/**
 * Scans a list of messages and returns context shifts — positions where
 * the embedded context changed compared to the previous context.
 *
 * Returns a Map keyed by the index of the USER message that precedes each
 * context message. This allows rendering the indicator above the user's
 * message rather than below it.
 *
 * Empty contexts are only shown when the user transitions from a non-empty
 * context to an empty one (i.e. navigated away). An initial empty context
 * at the start of a conversation is suppressed.
 */
export function detectContextShifts(
  messages: AgentThreadMessage[],
): Map<number, ClientViewingContext> {
  return scanForShifts(messages, 0, { previousContext: null }).shifts;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Index of the USER message the context at `startIndex` belongs to.
 *
 * Inclusive of `startIndex` itself: the context is now a block on the user's own message, so the
 * carrier and the target are the same message. On legacy threads the carrier is a ROBOTO message
 * written after the user's, and the loop steps back to it.
 */
function findPrecedingUserMessage(
  messages: AgentThreadMessage[],
  startIndex: number,
): number | null {
  for (let j = startIndex; j >= 0; j--) {
    if (messages[j].role === AgentThreadRole.USER) {
      return j;
    }
  }
  return null;
}

/**
 * Order-sensitive equality for a context's entity id lists. Order is
 * significant because the lists are built deterministically from the page's
 * viewing context, so a reordering would mean the underlying view changed.
 */
function idListsEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

function contextsEqual(
  a: ClientViewingContext | null,
  b: ClientViewingContext,
): boolean {
  if (a === null) {
    return false;
  }

  // Compare the entity id lists
  if (
    !idListsEqual(a.dataset_ids, b.dataset_ids) ||
    !idListsEqual(a.file_ids, b.file_ids) ||
    !idListsEqual(a.collection_ids, b.collection_ids) ||
    !idListsEqual(a.device_ids, b.device_ids) ||
    !idListsEqual(a.device_type_ids, b.device_type_ids) ||
    !idListsEqual(a.session_ids, b.session_ids) ||
    !idListsEqual(a.view_ids, b.view_ids)
  ) {
    return false;
  }

  // Which table is on screen is structural; its definition is not. It changes
  // with every filter the customer touches, so comparing it would render a
  // "context changed" indicator above nearly every message sent from a list.
  if ((a.table_state?.target ?? null) !== (b.table_state?.target ?? null)) {
    return false;
  }

  // Compare visualizer_state presence
  const aViz = a.visualizer_state;
  const bViz = b.visualizer_state;
  const aHasViz = aViz !== undefined && aViz !== null;
  const bHasViz = bViz !== undefined && bViz !== null;
  if (aHasViz !== bHasViz) {
    return false;
  }
  if (aHasViz && bHasViz) {
    // Live fields like the playhead advance with every message send, so a
    // raw deep-equal would treat every turn as a context shift and render a
    // "context changed" indicator above every user message. Strip volatile
    // fields first so only structural changes (panels added, file pinned,
    // etc.) count.
    if (
      JSON.stringify(stripVolatileVizFields(aViz)) !==
      JSON.stringify(stripVolatileVizFields(bViz))
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Returns a shallow copy of `visualizer_state` with the volatile keys
 * declared by the live-state bridge removed, so contextsEqual diffs only
 * structural state (panels, pinned files, etc.). Callers compare presence
 * before stripping, so `state` is always a real record here.
 */
function stripVolatileVizFields(
  state: Record<string, unknown>,
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (!VOLATILE_VISUALIZER_STATE_FIELDS.has(key)) {
      filtered[key] = value;
    }
  }
  return filtered;
}
